import React, { useMemo, type ComponentType } from 'react';
import escapeRegExp from 'lodash/escapeRegExp';
// eslint-disable-next-line @atlaskit/design-system/no-unsupported-drag-and-drop-libraries
import type { DraggableStateSnapshot } from 'react-beautiful-dnd';
import type { UIAnalyticsEvent } from '@atlaskit/analytics-next';
import type { ProjectContext } from '@atlassian/jira-router-resources-project-context/src/services/project-context/types.tsx';
import type { TenantContext } from '@atlassian/jira-shared-types/src/tenant-context.tsx';
import { type Group, type DO_NOT_ANIMATE_WIDTH, RESTRICTED_GROUP } from '../../../constants.tsx';
import type {
	FieldComponentType,
	FieldGroupComponentProps,
	DroppableLayoutItemListProps,
	LayoutItemsFilter,
	RestrictedFieldGroupComponentProps,
} from '../../../types.tsx';
import type { FieldCategoryWithFields } from '../../../types/layout-items.tsx';
import type { InactiveLayoutItemPanelProps } from '../../../types/ui.tsx';
import { type AvailableLayoutItemGroupWithName, useOnRenderLayoutItem } from '../utils.tsx';

export type Props = {
	// DnD props
	isDropDisabled: boolean;
	getIndexAfterUnsortableFields: (arg1: FieldCategoryWithFields, arg2: number) => number;
	droppableListIdPrefix: string;
	getDropAnimationWidth: (arg1: DraggableStateSnapshot) => number | typeof DO_NOT_ANIMATE_WIDTH;
	// entities
	filter: LayoutItemsFilter;
	project: ProjectContext;
	isRestrictedFieldsGroupVisible: boolean;
	readonly tenant: TenantContext;
	// injected components
	FieldComponent: FieldComponentType;
	PanelComponent: ComponentType<InactiveLayoutItemPanelProps>;
	DroppableLayoutItemList: ComponentType<DroppableLayoutItemListProps>;
	uiGroups: AvailableLayoutItemGroupWithName[];
	FieldGroupComponent: ComponentType<FieldGroupComponentProps>;
	RestrictedFieldGroupComponent: ComponentType<RestrictedFieldGroupComponentProps>;
	// callbacks
	onLoadMore: (
		group: Group,
		after: string,
		pageSize: number,
		analyticsEvent: UIAnalyticsEvent,
	) => void;
};

export const AvailableItemsList = (props: Props) => {
	const {
		isRestrictedFieldsGroupVisible,
		droppableListIdPrefix,
		isDropDisabled,
		getDropAnimationWidth,
		DroppableLayoutItemList,
		PanelComponent,
		FieldComponent,
		FieldGroupComponent,
		RestrictedFieldGroupComponent,
		project,
		getIndexAfterUnsortableFields,
		filter,
		onLoadMore,
		uiGroups,
		tenant,
	} = props;

	const highlightRegExp = useMemo(() => {
		if (!filter || !filter.name) {
			return null;
		}

		// Huh?
		const escaped = escapeRegExp(filter.name.replace(/\s{2,}/gi, ' '));
		return new RegExp(escaped, 'ig');
	}, [filter]);

	const onRenderItem = useOnRenderLayoutItem({
		FieldComponent,
		PanelComponent,
		project,
		getIndexAfterUnsortableFields,
		highlightRegExp,
	});

	const maxPageSize = 30;
	const groups = uiGroups.map(({ name, groupId, items, totalCount, endCursor, hasNextPage }) => {
		const remainingItems = totalCount - items.length;
		// +2 here to avoid situations when the last page will only have 1 field
		const pageSize = remainingItems < maxPageSize + 2 ? remainingItems : maxPageSize;

		return groupId === RESTRICTED_GROUP ? (
			isRestrictedFieldsGroupVisible && (
				// @ts-expect-error property `messages` is required for all groups even though it is only used in the suggested group
				<RestrictedFieldGroupComponent
					totalCount={totalCount}
					key={name}
					groupId={groupId}
					items={items}
					pageSize={pageSize}
					hasNextPage={hasNextPage}
					droppableListIdPrefix={droppableListIdPrefix}
					isDropDisabled={isDropDisabled}
					endCursor={endCursor}
					onLoadMore={onLoadMore}
					onRenderItem={onRenderItem}
					getDropAnimationWidth={getDropAnimationWidth}
					DroppableLayoutItemList={DroppableLayoutItemList}
					tenant={tenant}
				/>
			)
		) : (
			<FieldGroupComponent
				totalCount={totalCount}
				key={name}
				title={name}
				groupId={groupId}
				items={items}
				pageSize={pageSize}
				hasNextPage={hasNextPage}
				droppableListIdPrefix={droppableListIdPrefix}
				isDropDisabled={isDropDisabled}
				endCursor={endCursor}
				onLoadMore={onLoadMore}
				onRenderItem={onRenderItem}
				getDropAnimationWidth={getDropAnimationWidth}
				DroppableLayoutItemList={DroppableLayoutItemList}
			/>
		);
	});

	return <>{groups}</>;
};
